// What the numbers mean: score bands, targets and colors. The web app and the server both read these,
// so a score means the same thing on every screen and in every AI prompt.

export const CEFR_LEVELS = ['A1', 'A2', 'B1', 'B1+', 'B2', 'B2+', 'C1', 'C2'] as const;
export type Cefr = (typeof CEFR_LEVELS)[number];

export type Band = { min: number; label: string; meaning: string; level?: Cefr };

/** good: at or above target · ok: getting there · low: needs work */
export type Tone = 'good' | 'ok' | 'low';

export const SKILL_TARGET = 75; // B2: comfortable at work
export const PRON_TARGET = 80; // "Clear": easy to understand with a light accent

/** The score each goal aims for (new bands, 31 Oct 2026); pronunciation aims for PRON_TARGET whatever the goal. */
export const GOAL_TARGETS = {
  everyday: 65, // B1: gets the message across
  work: SKILL_TARGET, // B2
  interviews: SKILL_TARGET, // B2
  exams: 85, // C1: about IELTS 7
} as const;

/** Your target for this goal; SKILL_TARGET before you have one. */
export function targetFor(goal: keyof typeof GOAL_TARGETS | null | undefined): number {
  return goal ? GOAL_TARGETS[goal] : SKILL_TARGET;
}

/**
 * Score to CEFR level, one table for every screen and the AI prompt (server/lib/ai/speak-prompt.ts). Only real CEFR
 * levels, 10 points each from A2 to C1 (the owner's bands, 31 Oct 2026; before, B1 started at 45 and there was a B2+).
 * Where IELTS publishes a match, it follows it: B2 from about IELTS 5.5, C1 from 7, C2 from about 8.5. A level from
 * one short answer is an estimate, never an exam result. Earlier answers were moved onto these bands (migration 0025).
 */
export const SKILL_BANDS: readonly Band[] = [
  { min: 95, level: 'C2', label: 'Expert', meaning: 'Natural and precise, used with ease; errors are very rare.' },
  { min: 85, level: 'C1', label: 'Advanced', meaning: 'Fluent and precise, with a wide range and few errors.' },
  { min: 75, level: 'B2', label: 'Good', meaning: 'Clear and effective, with some small errors.' },
  { min: 65, level: 'B1', label: 'Developing', meaning: 'Simple language, frequent errors.' },
  { min: 55, level: 'A2', label: 'Basic', meaning: 'Short, simple sentences; hard to follow in places.' },
  { min: 0, level: 'A1', label: 'Beginner', meaning: 'Single words and set phrases; very hard to follow.' },
];

export const PRON_BANDS: readonly Band[] = [
  { min: 90, label: 'Very clear', meaning: 'Close to native-like and easy to understand.' },
  { min: 80, label: 'Clear', meaning: 'Easy to understand with a light accent.' },
  { min: 70, label: 'Understandable', meaning: 'People understand you, but some sounds are off.' },
  { min: 60, label: 'Often unclear', meaning: 'Listeners sometimes need you to repeat.' },
  { min: 0, label: 'Hard to understand', meaning: 'Many sounds need work.' },
];

/** fillersPer100: "uh" and "um" up to this many are normal in natural speech and cost nothing (5 since 31 Oct 2026). */
export const FLUENCY_TARGETS = { wpmMin: 110, wpmMax: 150, fillersPer100: 5, longPauses: 2 } as const;

export function bandOf(score: number, bands: readonly Band[] = SKILL_BANDS): Band {
  const band = bands.find((b) => score >= b.min) ?? bands.at(-1);
  if (!band) throw new Error('bands must not be empty');
  return band;
}

/** The CEFR level for a score (SKILL_BANDS). */
export function levelOf(score: number): Cefr {
  return bandOf(score).level ?? 'A1';
}

/**
 * Speak's overall score: the average of the rows on the result, the four criteria and pronunciation when it was
 * checked, rounded. Every row counts the same, as in IELTS. Without pronunciation, it is the average of the four, which
 * is also Write's score.
 */
export function speakOverall(criteria: readonly { score: number }[], pronunciation: number | null | undefined): number {
  const scores = [...criteria.map((c) => c.score), ...(pronunciation == null ? [] : [pronunciation])];
  return Math.round(scores.reduce((sum, n) => sum + n, 0) / scores.length);
}

export function toneOf(score: number, target: number = SKILL_TARGET): Tone {
  if (score >= target) return 'good';
  if (score >= 60) return 'ok';
  return 'low';
}
